import React from 'react';

const AboutUs = ({ onNavigate, onShowAuth }) => {
  return (
    <div className="min-h-screen bg-[#00162C] text-white">
      {/* Hero Section */}
      <section className="relative py-28 px-6 sm:px-8 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            {/* Left Content */}
            <div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Built for<br />
                business owners<br />
                <span className="bg-gradient-to-r from-[#0AB1B5] via-[#6DC066] to-[#7DF1EC] bg-clip-text text-transparent">
                  who've had enough
                </span>
              </h1>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-lg">
                Created by former payments insiders, RateIQ was built to expose the truth, level the playing field, and give business owners back control.
              </p>
              <button
                onClick={() => onNavigate('how-it-works')}
                className="bg-white hover:bg-gray-100 text-gray-900 font-medium px-8 py-3 rounded transition-colors duration-200"
              >
                How it Works
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The Story Behind Us Section */}
      <section className="py-16 px-6 sm:px-8 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The story behind us
            </h2>
            <p className="text-gray-300 text-lg max-w-4xl mx-auto leading-relaxed">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Analysis Card */}
            <div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                {/* Hidden Fees Alert */}
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Hidden Fees</h3>
                    <p className="text-gray-400 text-sm">Uncovered in analysis</p>
                  </div>
                </div>

                {/* Percentage Display */}
                <div className="mb-8">
                  <div className="text-5xl font-bold text-red-400 mb-2">+23%</div>
                  <p className="text-gray-400">of total volume</p>
                </div>

                {/* Fee Breakdown */}
                <div className="bg-white/5 rounded-xl p-6 mb-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-sm">Most from downgrade fees</p>
                        <p className="text-gray-400 text-xs">$1,247/mo in avoidable charges</p>
                      </div>
                      <span className="text-red-400">▶</span>
                    </div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-sm">Non-qualified rates</p>
                        <p className="text-gray-400 text-xs">$895/mo penalty fees</p>
                      </div>
                      <span className="text-red-400">▶</span>
                    </div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-sm">Markup on interchange</p>
                        <p className="text-gray-400 text-xs">$653/mo excess processor margin</p>
                      </div>
                      <span className="text-red-400">▶</span>
                    </div>
                  </div>
                </div>

                {/* Potential Savings */}
                <div className="bg-green-600/10 border border-green-600/30 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-green-400 font-semibold">Potential Savings</p>
                      <p className="text-green-300 text-xl font-bold">$2,795/month</p>
                    </div>
                    <span className="text-green-400">→</span>
                  </div>
                </div>

                {/* Avatar */}
                <div className="absolute top-6 right-6">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">K</div>
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">S</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Mission & Vision */}
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">Mission & Vision</h3>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  To expose hidden fees, simplify processing data, and give business owners the confidence to negotiate or switch.
                </p>
                <p>
                  A world where payment processing is transparent, fair, and competitive - where business owners call the shots.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-16 px-6 sm:px-8 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Header with CTA Button */}
          <div className="flex justify-between items-start mb-16">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                How it Works
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                Lorem ipsum dolor sit amet consectetur adipiscing elit blandit id dolor venenatis auctor maecenas egestas arcu ut consectetur.
              </p>
            </div>
            <button
              onClick={onShowAuth}
              className="bg-white hover:bg-gray-100 text-gray-900 font-medium px-8 py-3 rounded transition-colors duration-200 whitespace-nowrap"
            >
              Upload Statement
            </button>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <h3 className="text-white text-xl font-semibold mb-4">Step #1</h3>
              <p className="text-gray-300 leading-relaxed">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vel ut congue varius congue aliquet leo.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <h3 className="text-white text-xl font-semibold mb-4">Step #2</h3>
              <p className="text-gray-300 leading-relaxed">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vel ut congue varius congue aliquet leo.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <h3 className="text-white text-xl font-semibold mb-4">Step #3</h3>
              <p className="text-gray-300 leading-relaxed">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vel ut congue varius congue aliquet leo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What our clients say Section */}
      <section className="py-16 px-6 sm:px-8 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              What our clients say
            </h2>
            <p className="text-gray-300 text-lg max-w-4xl mx-auto leading-relaxed">
              Lorem ipsum dolor sit amet consectetur adipiscing elit blandit id dolor venenatis auctor maecenas egestas arcu ut consectetur.
            </p>
          </div>

          {/* Testimonial Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <div 
                  className="w-full h-96 bg-cover bg-center"
                  style={{
                    backgroundImage: "url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')"
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Right Side - Testimonial Card */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8">
                {/* Stars */}
                <div className="flex space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-orange-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-white text-xl font-medium mb-6 leading-relaxed">
                  "A delight to work with"
                </blockquote>

                {/* Testimonial Text */}
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit.
                </p>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  Exercitation veniam consequat sunt nostrud amet.
                </p>

                {/* Navigation Arrows */}
                <div className="flex space-x-4">
                  <button className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;